import { motion } from "framer-motion";
import { 
  Upload, 
  Brain, 
  ListChecks, 
  PenTool, 
  GitBranch, 
  Inbox, 
  BarChart3, 
  Users,
  Check,
  ArrowRight
} from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Section } from "@/components/layout/Section";
import { Container } from "@/components/layout/Container";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AnimatedSection } from "@/components/ui/animated-section";
import { slideInLeft, slideInRight, staggerContainer, staggerItem } from "@/lib/animations";
import { Link } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

// Feature data with all 8 features
const features = [
  {
    id: "document-upload",
    icon: Upload,
    title: "Document Upload & Processing",
    badge: "Core Feature",
    description: "Upload any document format and let our intelligent processing engine extract your content. We support PDFs, Word documents, images, and more. No manual data entry required.",
    capabilities: [
      "Support for PDF, DOCX, DOC, TXT, and image formats",
      "OCR technology for scanned documents",
      "Batch upload multiple files at once",
      "Automatic document structure detection",
      "Drag-and-drop interface for easy uploads",
      "Preview documents before processing",
    ],
  },
  {
    id: "ai-extraction",
    icon: Brain,
    title: "AI Question Extraction",
    badge: "AI Powered",
    description: "Our advanced AI understands the context and structure of your documents, automatically identifying questions, answer options, and survey logic with remarkable accuracy.",
    capabilities: [
      "Advanced AI-powered question identification",
      "Context-aware extraction that understands intent",
      "Handles complex question formats",
      "Extracts instructions and section headers",
      "Identifies required vs optional questions",
      "98%+ extraction accuracy on structured documents",
    ],
  },
  {
    id: "question-types",
    icon: ListChecks,
    title: "Question Type Detection",
    badge: "Smart Detection",
    description: "Automatically detects and categorizes question types, from multiple choice to matrix questions, rating scales to open-ended responses. No manual configuration needed.",
    capabilities: [
      "Multiple choice & single select detection",
      "Matrix and grid question support",
      "Rating scale identification (1-5, 1-10, etc.)",
      "Open-ended text question detection",
      "Ranking question recognition",
      "Checkbox and multi-select detection",
    ],
  },
  {
    id: "survey-editor",
    icon: PenTool,
    title: "Survey Editor",
    badge: "Full Control",
    description: "Fine-tune your extracted survey with our intuitive drag-and-drop editor. Reorder questions, edit text, adjust options, and perfect your survey before publishing.",
    capabilities: [
      "Drag-and-drop question reordering",
      "Rich text editing for questions and options",
      "Add, remove, or modify answer options",
      "Insert new questions manually",
      "Preview survey in real-time",
      "Undo/redo support for all changes",
    ],
  },
  {
    id: "logic-branching",
    icon: GitBranch,
    title: "Logic & Branching",
    badge: "Advanced",
    description: "Create sophisticated survey flows with conditional logic and branching. Show or hide questions based on previous answers, creating personalized experiences for each respondent.",
    capabilities: [
      "Skip logic based on answer selection",
      "Show/hide questions conditionally",
      "Multiple condition combinations (AND/OR)",
      "Branch to different survey sections",
      "Loop questions for repeated responses",
      "Piping answers into subsequent questions",
    ],
  },
  {
    id: "response-collection",
    icon: Inbox,
    title: "Response Collection",
    badge: "Distribution",
    description: "Share your surveys via unique links, embed them on your website, or distribute through multiple channels. Collect responses in real-time with progress tracking.",
    capabilities: [
      "Unique shareable survey links",
      "Embed surveys on any website",
      "Mobile-optimized response experience",
      "Real-time response notifications",
      "Save and continue later functionality",
      "Response validation and error prevention",
    ],
  },
  {
    id: "analytics-export",
    icon: BarChart3,
    title: "Analytics & Export",
    badge: "Insights",
    description: "Gain actionable insights with built-in analytics dashboards. View response summaries, charts, and trends. Export your data in multiple formats for further analysis.",
    capabilities: [
      "Real-time response dashboards",
      "Visual charts and graphs",
      "Response filtering and segmentation",
      "Export to CSV, Excel, and PDF",
      "Cross-tabulation analysis",
      "Custom report generation",
    ],
  },
  {
    id: "team-collaboration",
    icon: Users,
    title: "Team Collaboration",
    badge: "Enterprise",
    description: "Work together seamlessly with your team. Share surveys, assign roles, and collaborate on survey creation with built-in team management features.",
    capabilities: [
      "Invite team members to projects",
      "Role-based access control",
      "Comment and feedback on surveys",
      "Activity logs and audit trails",
      "Shared survey templates",
      "Team workspace management",
    ],
  },
];

export default function Features() {
  useDocumentMeta({
    title: 'Features',
    description: 'Explore SurviAI features: AI-powered extraction, smart question detection, easy distribution, powerful analytics, and enterprise security.'
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main>
        {/* Hero Section */}
        <Section className="pt-24 md:pt-32 pb-16">
          <Container>
            <AnimatedSection className="text-center max-w-4xl mx-auto">
              <Badge variant="secondary" className="mb-4">
                Platform Features
              </Badge>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                Everything You Need to <span className="text-gradient">Transform Surveys</span>
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                From document upload to response analytics, SurviAI provides a complete 
                survey creation and management platform powered by cutting-edge AI.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/auth">
                  <Button size="lg">
                    Get Started Free
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button size="lg" variant="outline">
                    View Pricing
                  </Button>
                </Link>
              </div>
            </AnimatedSection>
          </Container>
        </Section>

        {/* Features Overview Grid */}
        <Section background="muted">
          <Container>
            <AnimatedSection className="text-center mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-4">
                Feature Overview
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Click on any feature to learn more about its capabilities
              </p>
            </AnimatedSection>
            
            <motion.div 
              className="grid grid-cols-2 md:grid-cols-4 gap-4"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
            >
              {features.map((feature) => (
                <motion.a
                  key={feature.id}
                  href={`#${feature.id}`}
                  variants={staggerItem}
                  className="p-4 rounded-xl bg-background border hover:border-primary/50 hover:shadow-lg transition-all text-center group"
                >
                  <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mx-auto mb-3 group-hover:bg-primary/20 transition-colors">
                    <feature.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-medium text-foreground text-sm">{feature.title}</h3>
                </motion.a>
              ))}
            </motion.div>
          </Container>
        </Section>

        {/* Detailed Feature Sections */}
        {features.map((feature, index) => (
          <Section
            key={feature.id}
            id={feature.id}
            background={index % 2 === 0 ? "default" : "muted"}
          >
            <Container>
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? "lg:flex-row-reverse" : ""
              }`}>
                {/* Content */}
                <motion.div
                  variants={index % 2 === 0 ? slideInLeft : slideInRight}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-100px" }}
                  className={index % 2 === 1 ? "lg:order-2" : ""}
                >
                  <Badge variant="secondary" className="mb-4">
                    {feature.badge}
                  </Badge>
                  <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                    {feature.title}
                  </h2>
                  <p className="text-lg text-muted-foreground mb-6">
                    {feature.description}
                  </p>
                  
                  {/* Capabilities List */}
                  <ul className="space-y-3">
                    {feature.capabilities.map((capability, capIndex) => (
                      <li key={capIndex} className="flex items-start gap-3">
                        <div className="h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check className="h-3 w-3 text-primary" />
                        </div>
                        <span className="text-muted-foreground">{capability}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
                
                {/* Screenshot/Illustration Placeholder */}
                <motion.div
                  variants={index % 2 === 0 ? slideInRight : slideInLeft}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-100px" }}
                  className={index % 2 === 1 ? "lg:order-1" : ""}
                >
                  <div className="aspect-[4/3] rounded-xl bg-gradient-to-br from-primary/5 to-accent/5 border flex items-center justify-center overflow-hidden">
                    <div className="text-center p-8">
                      <feature.icon className="h-16 w-16 text-primary/30 mx-auto mb-4" />
                      <p className="text-muted-foreground text-sm">
                        {feature.title} Screenshot
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </Container>
          </Section>
        ))}

        {/* CTA Section */}
        <Section className="cta-gradient text-white">
          <Container>
            <AnimatedSection className="text-center max-w-3xl mx-auto py-8">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Ready to Transform Your Surveys?
              </h2>
              <p className="text-xl text-white/80 mb-8">
                Join thousands of researchers, educators, and teams who have already 
                streamlined their survey creation process with SurviAI.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/auth">
                  <Button size="lg" variant="secondary" className="bg-white text-primary hover:bg-white/90">
                    Start Free Trial
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                    Contact Sales
                  </Button>
                </Link>
              </div>
            </AnimatedSection>
          </Container>
        </Section>
      </main>

      <Footer />
    </div>
  );
}
